/**
 * Backoff-aware polling helper.
 *
 * Calls `fn` every `baseMs`. On failure, grows the interval (capped at `maxMs`)
 * so a downed backend doesn't spam the network or the console. On recovery,
 * snaps back to `baseMs`. Errors are only logged after `logAfter` consecutive
 * failures so a single hiccup stays silent.
 *
 * Also pauses while the page is hidden — there's no point polling in a
 * background tab.
 */
export function startPoller(name, fn, {baseMs, maxMs = 60000, logAfter = 3} = {}) {
    let failures = 0;
    let currentDelay = baseMs;
    let timer = null;
    let stopped = false;

    const schedule = (delay) => {
        if (stopped) return;
        timer = setTimeout(tick, delay);
    };

    const tick = async () => {
        if (stopped) return;
        if (document.hidden) {
            schedule(Math.min(currentDelay * 2, maxMs));
            return;
        }
        try {
            await fn();
            if (failures >= logAfter) {
                console.info(`[${name}] recovered after ${failures} failures`);
            }
            failures = 0;
            currentDelay = baseMs;
        } catch (err) {
            failures += 1;
            if (failures === logAfter) {
                console.warn(`[${name}] ${failures} consecutive failures: ${err.message || err}`);
            } else if (failures > logAfter && failures % 10 === 0) {
                console.warn(`[${name}] still failing (${failures}): ${err.message || err}`);
            }
            currentDelay = Math.min(currentDelay * 2, maxMs);
        }
        schedule(currentDelay);
    };

    // Fire once immediately, then schedule
    tick();

    return {
        stop() {
            stopped = true;
            if (timer) clearTimeout(timer);
        }
    };
}
